import React from 'react';

type Gender = 'male' | 'female';

type ActivityLevel = {
  label: string;
  value: number;
  description: string;
};

const ACTIVITY_LEVELS: ActivityLevel[] = [
  { label: 'Sedentary (1.2)', value: 1.2, description: 'Little to no exercise, desk job' },
  { label: 'Light (1.375)', value: 1.375, description: 'Light exercise 1-3 days/week' },
  { label: 'Moderate (1.55)', value: 1.55, description: 'Moderate exercise 3-5 days/week' },
  { label: 'Active (1.725)', value: 1.725, description: 'Hard exercise 6-7 days/week' },
];

export type WeightCalculatorResult = {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  ratePerWeek: number;
};

export type WeightCalculatorInputs = {
  currentWeight: number;
  targetWeight: number;
  height: number;
  age: number;
  gender: 'male' | 'female';
  timeframeMonths: number;
  activityLevel: number;
};

interface WeightCalculatorProps {
  initialInputs?: Partial<WeightCalculatorInputs>;
  saveButtonDisabled?: boolean;
  onSave?: (result: WeightCalculatorResult, inputs: {
    targetWeight: number;
    currentWeight: number;
    height: number;
    age: number;
    gender: 'male' | 'female';
    timeframeMonths: number;
    activityLevel: number;
  }) => void;
}

export const WeightCalculator: React.FC<WeightCalculatorProps> = ({ onSave, initialInputs, saveButtonDisabled = false }) => {
  const [currentWeight, setCurrentWeight] = React.useState<number>(() => initialInputs?.currentWeight ?? 80);
  const [targetWeight, setTargetWeight] = React.useState<number>(() => initialInputs?.targetWeight ?? 75);
  const [height, setHeight] = React.useState<number>(() => initialInputs?.height ?? 175);
  const [age, setAge] = React.useState<number>(() => initialInputs?.age ?? 30);
  const [gender, setGender] = React.useState<Gender>(() => (initialInputs?.gender as Gender) ?? 'male');
  const [timeframeMonths, setTimeframeMonths] = React.useState<number>(() => initialInputs?.timeframeMonths ?? 3);
  const [activityLevel, setActivityLevel] = React.useState<number>(() => initialInputs?.activityLevel ?? 1.55);

  React.useEffect(() => {
    if (!initialInputs) return;
    if (typeof initialInputs.currentWeight === 'number') setCurrentWeight(initialInputs.currentWeight);
    if (typeof initialInputs.targetWeight === 'number') setTargetWeight(initialInputs.targetWeight);
    if (typeof initialInputs.height === 'number') setHeight(initialInputs.height);
    if (typeof initialInputs.age === 'number') setAge(initialInputs.age);
    if (initialInputs.gender === 'male' || initialInputs.gender === 'female') setGender(initialInputs.gender);
    if (typeof initialInputs.timeframeMonths === 'number') setTimeframeMonths(initialInputs.timeframeMonths);
    if (typeof initialInputs.activityLevel === 'number') setActivityLevel(initialInputs.activityLevel);
  }, [initialInputs]);

  const computed = React.useMemo(() => {
    const safeCurrentWeight = Number.isFinite(currentWeight) ? currentWeight : 0;
    const safeTargetWeight = Number.isFinite(targetWeight) ? targetWeight : 0;
    const safeHeight = Number.isFinite(height) ? height : 0;
    const safeAge = Number.isFinite(age) ? age : 0;
    const safeTimeframeMonths = Number.isFinite(timeframeMonths) ? timeframeMonths : 0;
    const safeActivityLevel = Number.isFinite(activityLevel) ? activityLevel : 1;

    // BMR (Mifflin-St Jeor)
    const bmrBase = (10 * safeCurrentWeight) + (6.25 * safeHeight) - (5 * safeAge);
    const bmr = gender === 'male' ? bmrBase + 5 : bmrBase - 161;
    const tdee = bmr * safeActivityLevel;

    // Direction-aware daily delta
    const days = safeTimeframeMonths * 30.44;
    const totalKcalGap = (safeTargetWeight - safeCurrentWeight) * 7700; // negative for loss, positive for gain
    const dailyDelta = days > 0 ? (totalKcalGap / days) : 0;
    let dailyCalorieTarget = tdee + dailyDelta; // TDEE + delta handles deficit/surplus automatically

    // Safety floor
    const calorieFloor = gender === 'female' ? 1200 : 1500;
    const floorApplied = dailyCalorieTarget < calorieFloor;
    if (floorApplied) {
      dailyCalorieTarget = calorieFloor;
    }

    const weeks = safeTimeframeMonths * 4.345;
    const ratePerWeek = weeks > 0 ? (safeTargetWeight - safeCurrentWeight) / weeks : 0;

    // Goal direction for macro switching
    const isGaining = safeTargetWeight > safeCurrentWeight;
    const isLosing = safeTargetWeight < safeCurrentWeight;

    // Macro allocation
    let protein = 0;
    let fats = 0;
    let carbs = 0;

    if (isGaining) {
      protein = 1.8 * safeCurrentWeight;
      fats = (0.25 * dailyCalorieTarget) / 9;
      carbs = (dailyCalorieTarget - (protein * 4) - (fats * 9)) / 4;
    } else if (isLosing) {
      protein = 2.2 * safeCurrentWeight;
      fats = (0.20 * dailyCalorieTarget) / 9;
      carbs = (dailyCalorieTarget - (protein * 4) - (fats * 9)) / 4;
    } else {
      protein = 2.0 * safeCurrentWeight;
      fats = (0.22 * dailyCalorieTarget) / 9;
      carbs = (dailyCalorieTarget - (protein * 4) - (fats * 9)) / 4;
    }

    const rounded: WeightCalculatorResult = {
      calories: Math.round(dailyCalorieTarget),
      protein: Math.max(0, Math.round(protein)),
      fats: Math.max(0, Math.round(fats)),
      carbs: Math.max(0, Math.round(carbs)),
      ratePerWeek: Number(ratePerWeek.toFixed(2)),
    };

    const lossRateTooFast = isLosing && safeCurrentWeight > 0
      ? ((-ratePerWeek) / safeCurrentWeight) > 0.01
      : false;

    const warnings: string[] = [];
    if (floorApplied) {
      warnings.push('Warning: Timeframe is too short for metabolic safety.');
    }
    if (lossRateTooFast) {
      warnings.push('Suggestion: Weight loss rate exceeds ~1% body weight/week. Consider a longer timeframe.');
    }

    return {
      bmr,
      tdee,
      dailyDelta,
      totalWeightChange: safeTargetWeight - safeCurrentWeight,
      result: rounded,
      warnings,
    };
  }, [
    currentWeight,
    targetWeight,
    height,
    age,
    gender,
    timeframeMonths,
    activityLevel,
  ]);

  return (
    <div style={{
      marginTop: 16,
      padding: 16,
      borderRadius: 8,
      backgroundColor: 'var(--color-dark-gray, #2a2a2a)',
      borderLeft: '3px solid var(--color-primary, #4CAF50)',
    }}>
      <h2 style={{ margin: '0 0 12px 0', fontSize: 20, fontWeight: 600, color: 'var(--color-primary, #4CAF50)' }}>
        Weight Goal Calculator - that uses some dumb rules found on 'the internet', consult a specialist for proper information.
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Current weight (kg)
          <input
            type="number"
            value={currentWeight}
            onChange={(e) => setCurrentWeight(Number(e.target.value))}
            min={0}
            step={0.1}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Target weight (kg)
          <input
            type="number"
            value={targetWeight}
            onChange={(e) => setTargetWeight(Number(e.target.value))}
            min={0}
            step={0.1}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Height (cm)
          <input
            type="number"
            value={height}
            onChange={(e) => setHeight(Number(e.target.value))}
            min={0}
            step={1}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Age (years)
          <input
            type="number"
            value={age}
            onChange={(e) => setAge(Number(e.target.value))}
            min={0}
            step={1}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Gender
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value as Gender)}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Timeframe (months)
          <input
            type="number"
            value={timeframeMonths}
            onChange={(e) => setTimeframeMonths(Number(e.target.value))}
            min={0}
            step={1}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#ccc' }}>
          Activity level
          <select
            value={activityLevel}
            onChange={(e) => setActivityLevel(Number(e.target.value))}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #3a3a3a', background: '#111', color: '#fff' }}
          >
            {ACTIVITY_LEVELS.map((lvl) => (
              <option key={lvl.value} value={lvl.value}>
                {lvl.label} - {lvl.description}
              </option>
            ))}
          </select>
        </label>
      </div>

      {computed.warnings.length > 0 && (
        <div style={{ marginTop: 12, color: '#ffcc66', fontSize: 13, lineHeight: 1.4 }}>
          {computed.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ background: '#111', border: '1px solid #3a3a3a', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Calories / day</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{computed.result.calories}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #3a3a3a', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Protein (g)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{computed.result.protein}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #3a3a3a', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Carbs (g)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{computed.result.carbs}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #3a3a3a', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Fats (g)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{computed.result.fats}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #3a3a3a', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Rate (kg/week)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{computed.result.ratePerWeek}</div>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        BMR: {Math.round(computed.bmr)} | TDEE: {Math.round(computed.tdee)} | Daily delta: {Math.round(computed.dailyDelta)}
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
        <strong>What's this?</strong><br />
        • <strong>BMR</strong> (Basal Metabolic Rate): calories your body burns at rest.<br />
        • <strong>TDEE</strong> (Total Daily Energy Expenditure): BMR × activity level = calories you burn per day.<br />
        • <strong>Daily delta</strong>: extra calories you need each day to reach your goal in the given timeframe.
      </div>
      {onSave && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            disabled={saveButtonDisabled}
            onClick={() => onSave?.(computed.result, {
              targetWeight,
              currentWeight,
              height,
              age,
              gender,
              timeframeMonths,
              activityLevel,
            })}
            style={{
              padding: '12px 24px',
              background: 'var(--color-primary, #4CAF50)',
              border: 'none',
              borderRadius: 8,
              color: '#0b0b0b',
              fontSize: 16,
              fontWeight: 600,
              cursor: saveButtonDisabled ? 'not-allowed' : 'pointer',
              opacity: saveButtonDisabled ? 0.65 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            Save Goal
          </button>
        </div>
      )}
    </div>
  );
};
